/**
 * Primera vez: Donna recién instalada y sin ninguna clínica. Se crea la clínica y la cuenta de quien la
 * administra; luego se sigue con el logo, los usuarios y el resto en ⚙ Configuración.
 */
import { useState, type FormEvent } from "react";
import { api, ErrorApi, type Sesion } from "../api.ts";
import { Logo, AvisoError, CampoClave } from "./comun.tsx";
import { correoValido, rucValido, telefonoValido } from "../../../dominio/src/index.ts";

export function Bienvenida({ alTerminar, requiereCodigo = false }: { alTerminar: (s: Sesion) => void; requiereCodigo?: boolean }) {
  const [f, setF] = useState({ codigo: "", clinica: "", ruc: "", ciudad: "", direccion: "", telefono: "", nombre: "", email: "", contrasena: "", repetir: "", registro: "" });
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  function problemas(): string[] {
    const p: string[] = [];
    if (requiereCodigo && !f.codigo.trim()) p.push("Escribe el código de instalación.");
    if (f.clinica.trim().length < 3) p.push("Escribe el nombre de la clínica.");
    if (f.ruc && !rucValido(f.ruc.trim())) p.push("El RUC no es válido (13 dígitos).");
    if (f.telefono && !telefonoValido(f.telefono)) p.push("El teléfono debe ser un celular (09XXXXXXXX) o un fijo con su código (02XXXXXXX).");
    if (f.nombre.trim().length < 3) p.push("Escribe tu nombre completo.");
    if (!correoValido(f.email.trim())) p.push("Revisa el correo.");
    if (f.contrasena.length < 10 || !/[a-zA-Z]/.test(f.contrasena) || !/\d/.test(f.contrasena)) p.push("La contraseña debe tener al menos 10 caracteres, con letras y números.");
    if (f.contrasena !== f.repetir) p.push("Las contraseñas no coinciden.");
    return p;
  }
  async function enviar(e: FormEvent) {
    e.preventDefault();
    const p = problemas();
    if (p.length) return setError(new ErrorApi(400, "Revisa los datos.", p));
    setError(null); setEnviando(true);
    try { alTerminar(await api<Sesion>("POST", "/api/instalacion", f)); }
    catch (err) { setError(err); }
    finally { setEnviando(false); }
  }

  return (
    <main className="login bienvenida">
      <form className="card" onSubmit={enviar} aria-labelledby="t-bienvenida">
        <div className="brand" style={{ color: "var(--accent)" }}><Logo /><span style={{ color: "var(--ink)" }}>Donna</span></div>
        <h1 id="t-bienvenida" style={{ fontSize: 22, margin: 0 }}>Bienvenido: configura tu clínica</h1>
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>Es la primera vez que se abre Donna en esta computadora. Estos datos salen en recetas, certificados y recibos; los puedes cambiar después en ⚙ Configuración.</p>
        {requiereCodigo && (
          <label className="campo">Código de instalación *<input className="mono" required value={f.codigo} onChange={set("codigo")} autoComplete="off" spellCheck={false} />
            <small className="hint">Lo mostró el instalador del servidor al terminar. Protege a Donna para que nadie más cree la primera cuenta.</small></label>
        )}
        <fieldset className="grupo-bienvenida">
          <legend>Tu clínica</legend>
          <label className="campo">Nombre de la clínica *<input required value={f.clinica} onChange={set("clinica")} autoComplete="organization" /></label>
          <div className="rejilla" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))" }}>
            <label className="campo">RUC<input className="mono" inputMode="numeric" maxLength={13} value={f.ruc} onChange={set("ruc")} /></label>
            <label className="campo">Teléfono<input className="mono" inputMode="tel" value={f.telefono} onChange={set("telefono")} /></label>
            <label className="campo">Ciudad<input value={f.ciudad} onChange={set("ciudad")} /></label>
          </div>
          <label className="campo">Dirección<input value={f.direccion} onChange={set("direccion")} autoComplete="street-address" /></label>
        </fieldset>
        <fieldset className="grupo-bienvenida">
          <legend>Tu cuenta (administrador)</legend>
          <label className="campo">Nombre completo *<input required value={f.nombre} onChange={set("nombre")} autoComplete="name" placeholder="Dra. Andrea Molina" /></label>
          <label className="campo">Correo *<input required type="email" value={f.email} onChange={set("email")} autoComplete="username" /></label>
          <div className="rejilla" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))" }}>
            <CampoClave etiqueta="Contraseña *" className="" required value={f.contrasena} onChange={(v) => setF({ ...f, contrasena: v })} autoComplete="new-password" />
            <CampoClave etiqueta="Repite la contraseña *" className="" required value={f.repetir} onChange={(v) => setF({ ...f, repetir: v })} autoComplete="new-password" />
          </div>
          <small className="hint">Al menos 10 caracteres, con letras y números. Guárdala bien: con ella se crean los demás usuarios.</small>
          <label className="campo">Registro profesional (si también atiendes pacientes)<input value={f.registro} onChange={set("registro")} placeholder="MSP 1703-12-45678" /></label>
        </fieldset>
        <AvisoError error={error} />
        <button className="btn pri" disabled={enviando}>{enviando ? "Preparando Donna…" : "Empezar a usar Donna"}</button>
      </form>
    </main>
  );
}
